// SessionExpiredModal — Global modal som visas när mod-token har gått ut.
// Etappe 4.12 — Token-Expiry-skydd för moderationsbereich.
//
// Lyssnar på `mod:session-expired`-eventet från utils/modAxios.js.
// Visar svenskt meddelande, erbjuder „Logga in igen" som tar moderatorn till
// /mod/login?returnTo=<aktuell sida>. Eventuellt sparat utkast återställs
// av målsidan via utils/modAxios.readPendingDraft().
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, LogIn } from "lucide-react";

export default function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [returnTo, setReturnTo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    function onExpired(e) {
      setHasDraft(!!e?.detail?.hasDraft);
      setReturnTo(e?.detail?.returnTo || null);
      setOpen(true);
    }
    window.addEventListener("mod:session-expired", onExpired);
    return () => window.removeEventListener("mod:session-expired", onExpired);
  }, []);

  if (!open) return null;

  const handleLogin = () => {
    setOpen(false);
    const target = returnTo
      ? `/mod/login?returnTo=${encodeURIComponent(returnTo)}`
      : "/mod/login";
    navigate(target);
  };

  return (
    <div
      data-testid="session-expired-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink/60 px-4 py-6"
    >
      <div className="bg-parchment border border-warm-border rounded-sm shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-3">
          <AlertCircle size={22} className="text-falu shrink-0 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-heading text-lg font-semibold text-ink mb-1">
              Din moderatorsession har gått ut
            </h2>
            <p className="text-sm text-bark leading-relaxed">
              {hasDraft
                ? "Dina ändringar är sparade tillfälligt i webbläsaren. Logga in igen så återställs utkastet och du kan spara på nytt."
                : "Logga in igen för att fortsätta arbeta."}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            data-testid="session-expired-login"
            onClick={handleLogin}
            className="inline-flex items-center justify-center gap-2 bg-pine hover:bg-pine-hover text-white font-medium text-sm px-4 py-2.5 rounded-md transition-colors"
          >
            <LogIn size={15} /> Logga in igen
          </button>
        </div>

        {hasDraft && (
          <p className="text-[11px] text-bark/60 mt-4 leading-relaxed">
            Utkastet ligger lokalt i din webbläsare (localStorage) och tas bort så snart du
            har sparat på nytt. Stäng inte fliken förrän du är klar.
          </p>
        )}
      </div>
    </div>
  );
}
